import { useEffect, useMemo, useState } from 'react';

import { Drawer, Typography } from '@material-tailwind/react';
import { VariantProps, cva } from 'class-variance-authority';
import Cookies from 'js-cookie';
import { Link, useLocation } from 'react-router-dom';

import { api } from '@plugins/axios/api';

import { useMediaQuery } from '@hooks/useMediaQuery';
import { useRecoil } from '@hooks/useRecoil';

import { Button } from '@components/button/Button';
import { DropAreaHeader } from '@components/header/parts/DropAreaHeader';
import { DropBoxHeader } from '@components/header/parts/DropBoxHeader';
import { NoDepthHeader } from '@components/header/parts/NoDepthHeader';
import { MNavLinkProps } from '@components/link/MNavLink';

import LogoImg from '@assets/svgs/common/logo.svg';

// import LogoWhiteImg from '@assets/imgs/common/logo_white.png';

interface NavUnit extends Pick<MNavLinkProps, 'end' | 'hash' | 'rootInclude'> {
  title: string;
  link?: string;
  list?: NavUnit[];
}

export interface NavItem extends NavUnit {
  depth?: number; // navUnit 최대 중첩 반복 횟수
  onClick?: () => void;
}

export const TOTAL_NAV_ITEM_LIST: { [name: string]: NavItem[] } = {
  funeral: [
    {
      title: '메인',
      link: 'funeral/main',
    },
    {
      title: '주문 내역(현황)',
      link: 'funeral/orders',
    },
    {
      title: '다회용기 주문',
      link: 'funeral/new-order',
    },
  ],
  partner: [
    {
      title: '메인',
      link: 'partner/main',
    },
    {
      title: '미접수 주문 내역',
      link: 'partner/unknown-orders',
    },
    {
      title: '접수 주문 내역',
      link: 'partner/accept-orders',
    },
    // {
    //   title: '회수 관리',
    //   link: 'partner/recovery',
    // },
  ],
};

const HeaderWrapperVariants = cva('', {
  variants: {
    type: {
      'no-depth': 'no-depth',
      'drop-box': 'drop-box',
      'drop-area': 'drop-area',
    },
    topGap: {
      'header-height': 'header-height',
      'none': 'none',
    },
    mMenu: {
      drower: 'drower',
    },
  },
  defaultVariants: {
    type: 'drop-area',
    topGap: 'header-height',
    mMenu: 'drower',
  },
});

export interface HeaderWrapperProps
  extends VariantProps<typeof HeaderWrapperVariants> {}

export const HeaderWrapper = ({ type, topGap, mMenu }: HeaderWrapperProps) => {
  const [open, setOpen] = useState(false);
  const { isMedia } = useMediaQuery();
  const openMenu = () => setOpen(true);
  const closeMenu = () => setOpen(false);
  const location = useLocation();
  useEffect(() => {
    //url 변경시 슬라이드 메뉴 닫힘 처리
    closeMenu();
  }, [location]);

  const wrapperProps = useMemo(
    () => HeaderWrapperVariants({ type, topGap, mMenu }),
    [type, topGap, mMenu],
  );

  const { useRecoilState, useAtom } = useRecoil();
  const [profile, setProfile] = useRecoilState(
    useAtom<{ uid: number; role: 'partner' | 'funeral'; name: string } | null>(
      'USER_PROFILE',
    ),
  );

  const NAV_ITEM_LIST: NavItem[] = useMemo(() => {
    if (profile == null) {
      return [];
    }

    return TOTAL_NAV_ITEM_LIST[profile.role];
  }, [profile]);

  return (
    <>
      {/* sticky 헤더일 경우 body 의 빈부분을 채워주는 역할 */}
      {wrapperProps.includes('header-height') && (
        <div
          className={`flex h-[theme(var.header.height.mobile)] w-full md:h-[theme(var.header.height.pc)]`}
        />
      )}

      {/* header 타입에 따른 구분 */}
      {wrapperProps.includes('no-depth') && (
        <NoDepthHeader
          list={NAV_ITEM_LIST}
          mMenuOpenFn={openMenu}
          logoImgSrc={LogoImg}
        />
      )}
      {wrapperProps.includes('drop-area') && (
        <DropAreaHeader
          list={NAV_ITEM_LIST}
          mMenuOpenFn={openMenu}
          logoImgSrc={LogoImg}
          // logoReverseImgSrc={LogoWhiteImg}
        />
      )}
      {wrapperProps.includes('drop-box') && (
        <DropBoxHeader
          list={NAV_ITEM_LIST}
          mMenuOpenFn={openMenu}
          logoImgSrc={LogoImg}
          // logoReverseImgSrc={LogoWhiteImg}
        />
      )}

      {/* mobile menu 설정에 따른 구분 */}
      {wrapperProps.includes('drower') && isMedia('sm', 'less') && (
        <Drawer
          overlayProps={{ className: 'fixed' }}
          open={open}
          placement='right'
          onClose={closeMenu}
        >
          <div className='py-6 pr-8'>
            <Typography className='my-auto h-fit text-right align-middle text-xl font-bold tracking-tight text-[#333333]'>
              {profile != null ? profile.name : ''}
              <span className='pl-1 text-[#353535]'>님</span>
            </Typography>
            <div className='mt-2 flex w-full justify-end'>
              <span>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    api.post('/auth/logout').then(() => {
                      Cookies.remove('REPLHADA_ADMIN_ACCESS_TOKEN');
                      Cookies.remove('REPLHADA_ADMIN_REFRESH_TOKEN');
                      setProfile(null);
                    });
                  }}
                >
                  로그아웃
                </Button>
              </span>
            </div>
            <hr className='dark:bg-gray-700" my-4 h-px border-0 bg-gray-200' />
            <ul className='flex flex-col items-end'>
              {NAV_ITEM_LIST.map((item, i) => {
                return (
                  <li key={`nav-item-${i}`} className='my-4 flex'>
                    <Link
                      to={item.link ?? '.'}
                      className='my-auto h-fit cursor-pointer text-center align-middle text-lg font-semibold leading-[1.1rem] text-[#5c5c5c]'
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </Drawer>
      )}
    </>
  );
};
